import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import get from 'lodash/get'
import has from 'lodash/has'
import MVT from 'ol/format/MVT'
import VectorTileSource from 'ol/source/VectorTile'
import { stylefunction } from 'ol-mapbox-style'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import PoziAPIData from 'api/data'
import * as PoziUtils from 'api/utils'
import GeoJSONIO from 'data/geojsonio'
import { setLayerLoadDone } from 'drawer/tabs/layers/actions'
import PoziEvents from 'utils/events'

const styles = theme => ({
  root: {}
})

class VectorTile extends React.Component {
  layerLoaded = false

  currentTarget = null

  layerConf = null

  selectedFeatureId = null

  updatingStyles = false

  loadStyle (glStyle) {
    const { olLayer, layerConf } = this.props

    let url = get(layerConf, 'config.spatial.url', '')
    if (url === '') {
      console.log('No URL defined in config for VectorTile!', layerConf.title)
      return
    }

    // feature property to act as identifier
    const featureId = get(layerConf, 'config.spatial.id', '')
    if (featureId === '') {
      console.log('ID not defined for VectorLayer!', layerConf.title)
      return
    }

    console.log('mapboxStyle loaded', layerConf.titleid, glStyle)
    // https://github.com/openlayers/ol-mapbox-style
    // How to detect the source name? Is this even necessary?
    // The first style in the mapbox layers array often seems to be 'background', which does not have a source element.
    // Get the first valid source name?
    const glStyleLayers = get(glStyle, 'layers', [])
    const glStyleSources = get(glStyle, 'sources', [])
    let layerid = ''
    const layerids = []
    let sourcesURL = ''
    for (const key in glStyleSources) {
      if (Object.prototype.hasOwnProperty.call(glStyleSources, key)) {
        const source = glStyleSources[key]
        const sourceURL = (source.url || '').replace('mapbox://', '')
        if (sourcesURL !== '') {
          sourcesURL += ','
        }
        sourcesURL += sourceURL
      }
    }
    url = url.replace('{sources}', sourcesURL)

    for (let j = 0; j < glStyleLayers.length; j++) {
      const styleLayer = glStyleLayers[j]
      // sourceName = get(styleLayer, 'source-layer', '')
      if (has(styleLayer, 'source')) {
        layerid = get(styleLayer, 'id', '')
        layerids.push(layerid)
      }
    }

    // stylefunction returns a function with signature f(feature, resolution)
    // olStyleFunction(f,r) returns an array of olStyle objects.
    let olStyleFunction = null
    if (get(glStyle, 'version', null)) {
      olStyleFunction = stylefunction(olLayer, glStyle, layerids)
    }
    const olStyleFunctionWithSelection = function (feature, resolution) {
      let stylesMB = []
      if (olStyleFunction) {
        stylesMB = olStyleFunction(feature, resolution) || [] // array of olStyle
      }
      let styles = [] // Selected styles
      // See ./map/SelectVectorFeature - Triggers a style refresh with: olLayer.setStyle(olLayer.getStyle())
      const currentTarget = this.currentTarget
      const layerConf = this.layerConf
      let compareValue = ''
      if (currentTarget?.propertiesId) {
        compareValue = currentTarget.propertiesId
      }
      const featureIdValue = PoziUtils.getFeaturePropertyValueCaseInsensitive(feature, featureId)
      if (currentTarget && layerConf && layerConf.selected && currentTarget.titleid === layerConf.titleid && compareValue !== '' && featureIdValue === compareValue) {
        // console.log('Set style to selected', layer.titleid, featureIdValue)
        const styleSelected = GeoJSONIO().getStyle(layerConf, feature, resolution, true)
        styles = [...styles, ...styleSelected]
      } else if (stylesMB.length === 0) {
        // console.log('Set style to unselected', layer.titleid, featureIdValue)
        const styleUnselected = GeoJSONIO().getStyle(layerConf, feature, resolution, false)
        styles = [...styles, ...styleUnselected]
      } else {
        // Style should be set to whatever is defined in the mapbox style.
      }
      const allStyles = [...styles, ...stylesMB] // Combine two arrays ES5
      return allStyles
    }.bind(this)

    const preventClick = get(layerConf, 'preventClick', true)
    const style = preventClick ? olStyleFunction : olStyleFunctionWithSelection
    olLayer.setStyle(style)

    const olSourceVT = new VectorTileSource({
      format: new MVT(),
      url
    })

    olLayer.setSource(olSourceVT)

    PoziUtils.setSourceLoaded(olLayer, olSourceVT, layerConf)

    // Mark layer as loaded when either one tile has successfully loaded
    // or failed
    olSourceVT.once(['tileloadend', 'tileloaderror'], () => {
      window.store.dispatch(setLayerLoadDone(layerConf.titleid))
    })

    olSourceVT.on('tileloaderror', () => {
      const errorMsg = 'Layer load failed ' + layerConf.title

      PoziUtils.gtag({
        category: 'data',
        status: 'warning',
        identifier: layerConf.title,
        action: 'ajax request',
        additionalInfo: errorMsg
      })

      console.log('Unable to load a vector tile for layer ', layerConf.title)
    })

    PoziEvents().onLayerVisible(olLayer, PoziUtils.isLayerSelected(layerConf))

    // if (olLayer) {
    // window.appObject.map.addLayer(olLayer)
    // }
  }

  addLayer () {
    const layerConf = this.layerConf
    const mapboxStyle = get(layerConf, 'style.mapbox', '')
    const loadStyle = this.loadStyle.bind(this)

    if (mapboxStyle === '') {
      loadStyle() // Uses GeoJSON.io styling.
    } else {
      PoziAPIData().ajax({
        url: mapboxStyle,
        error: function (jqXHR, textStatus, errorThrown) {
          console.error('VectorTile mapboxStyle failed to load', layerConf.titleid)
          // TODO: Update state.
          loadStyle() // Uses GeoJSON.io styling.
        },
        success: function (glStyle, textStatus, jqXHR) {
          console.info('VectorTile mapbox style loaded', glStyle)
          loadStyle(glStyle)
        }
      })
    }
  }

  stylesUpdated () {
    this.updatingStyles = false
  }

  render () {
    const { bMapLoaded, layerConf, currentTarget, olLayer } = this.props

    if (!bMapLoaded || !layerConf /* || !layer.selected  // TODO: need to remove dependancy on olMap.getLayers() */) {
      return null
    }

    this.currentTarget = currentTarget
    this.layerConf = layerConf // selected flag

    if (!this.layerLoaded) {
      this.layerLoaded = true
      this.addLayer()
    }

    // To allow access to dynamic values in non-react component (openlayers render function)
    const featureId = get(layerConf, 'config.spatial.id', null)

    if (featureId !== null && currentTarget?.propertiesId && olLayer && layerConf.selected) {
      console.log('updatingStyles', layerConf.titleid)
      olLayer.setStyle(olLayer.getStyle()) // Triggers a layer style refresh (olStyleFunctionWithSelection)
    }

    return null
  }
}

VectorTile.propTypes = {
  classes: PropTypes.object.isRequired,
  bMapLoaded: PropTypes.bool.isRequired,
  olLayer: PropTypes.object.isRequired,
  layerConf: PropTypes.object.isRequired,
  currentTarget: PropTypes.object,
  layer: PropTypes.object,
  titleid: PropTypes.string.isRequired
}

function mapStateToProps (state, ownProps) {
  return {
    bMapLoaded: state.bMapLoaded,
    currentTarget: state.currentTarget
  }
}

const mapDispatchToProps = dispatch => {
  return {}
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(VectorTile)
  )
)
